import { Suspense, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@hulahoop/ui";
import type { useOrganizations } from "@/application/workflows/organization";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { ShellBoundary } from "./ShellBoundary";
import { SettingsSidebar } from "./SettingsSidebar";
import { SHELL_CONTENT_ID } from "./shellNavigation";

interface SettingsShellProps {
  organizations: ReturnType<typeof useOrganizations>;
  loadingFallback: ReactNode;
}

export function SettingsShell({ organizations, loadingFallback }: SettingsShellProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const selectedUuid = useOrganizationStore((state) => state.selectedOrganizationUuid);
  const loading = organizations.isSwitching || !selectedUuid;
  return (
    <div className="flex h-dvh">
      <a href={`#${SHELL_CONTENT_ID}`} className="sr-only focus:not-sr-only">
        {t("shell.skip_navigation")}
      </a>
      <SettingsSidebar />
      <main
        id={SHELL_CONTENT_ID}
        tabIndex={-1}
        className="min-w-0 flex-1 overflow-y-auto bg-background"
      >
        <div className="mx-auto w-full max-w-3xl px-8 pb-16 pt-16">
          {organizations.error ? (
            <div role="alert" className="flex flex-col items-start gap-2">
              <p>{t("shell.organizations_failed")}</p>
              <Button onClick={organizations.retryUserOrganizations}>{t("shell.retry")}</Button>
            </div>
          ) : loading ? (
            loadingFallback
          ) : (
            <Suspense fallback={loadingFallback}>
              <ShellBoundary
                key={pathname}
                message={t("shell.page_failed")}
                className="flex flex-col gap-2"
              >
                <Outlet />
              </ShellBoundary>
            </Suspense>
          )}
        </div>
      </main>
    </div>
  );
}
